import React, { useState } from "react";
import { Utensils, Droplets, Zap, Store } from "lucide-react";
import { callBackend } from "../utils/api";
import { useNotify } from "./Notification";

const CONSUMABLES = [
  {
    id: 202,
    name: "Bottled Water",
    price: 50,
    thirst: 25,
    hunger: 0,
    icon: <Droplets size={18} />,
  },
  {
    id: 204,
    name: "Gas Station Burrito",
    price: 80,
    thirst: -5,
    hunger: 30,
    icon: <Utensils size={18} />,
  },
  {
    id: 203,
    name: "Energy Drink",
    price: 150,
    thirst: 15,
    hunger: 5,
    icon: <Zap size={18} />,
  },
  {
    id: 205,
    name: "Double Bacon Burger",
    price: 250,
    thirst: -10,
    hunger: 60,
    icon: <Utensils size={18} />,
  },
  {
    id: 207,
    name: "Sports Drink",
    price: 400,
    thirst: 50,
    hunger: 0,
    icon: <Droplets size={18} />,
  },
  {
    id: 206,
    name: "Steak Meal",
    price: 1200,
    thirst: -5,
    hunger: 100,
    icon: <Utensils size={18} />,
  },
];

export default function SevenEleven({ stats, onPurchaseSuccess }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const { addNotification } = useNotify(); // Initialize notification helper

  const handleBuy = async (item) => {
    if (stats.cash < item.price) return;
    setIsProcessing(true);
    try {
      // Pointing to the new unified endpoint
      const response = await callBackend("/shop/buy", {
        method: "POST",
        body: { itemId: item.id, quantity: 1 },
      });

      // Show the notification and trigger ticker tape
      addNotification(response.message, "success", "cash");

      onPurchaseSuccess();
    } catch (err) {
      // Notify on error as well
      addNotification(
        err.message || "Store clerk refused service",
        "error",
        "error",
      );
      console.error("Store clerk refused service:", err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="border-b-2 border-zinc-900 pb-6">
        <h2 className="text-4xl font-black italic uppercase text-yellow-500 flex items-center gap-3">
          <Store size={36} /> 7-Eleven
        </h2>
        <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-[0.2em]">
          Open 24/7. No questions asked.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {CONSUMABLES.map((item) => (
          <div
            key={item.id}
            className="bg-zinc-950 border border-zinc-900 p-4 hover:border-yellow-900/50 transition-all group"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-3 bg-zinc-900 border border-zinc-800 text-yellow-500">
                {item.icon}
              </div>
              <div className="text-right">
                <div className="text-xs font-black text-zinc-500 uppercase">
                  {item.name}
                </div>
                <div className="text-xl font-black text-white italic">
                  ${item.price}
                </div>
              </div>
            </div>

            <div className="flex gap-4 mb-4 text-[9px] font-bold uppercase">
              <div className="flex items-center gap-1 text-orange-500">
                <Utensils size={10} /> +{item.hunger} Nut
              </div>
              <div className="flex items-center gap-1 text-blue-500">
                <Droplets size={10} /> +{item.thirst} Hyd
              </div>
            </div>

            <button
              disabled={isProcessing || stats.cash < item.price}
              onClick={() => handleBuy(item)}
              className={`w-full py-2 font-black uppercase italic skew-x-[-12deg] transition-all border-2 
                ${
                  stats.cash >= item.price
                    ? "border-yellow-600 bg-yellow-600 text-black hover:bg-white hover:border-white"
                    : "border-zinc-800 text-zinc-600 cursor-not-allowed"
                }`}
            >
              {stats.cash >= item.price ? "Purchase" : "Broke"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
